// The Base upsell, to the mockup's compare design: two upgrade cards —
// one big, one small, trading places as the family considers them — with
// hand-authored "what Base doesn't include" deltas, an inline payment
// panel once a switch is picked, and the keep-Base band as the quiet
// bottom path. Prices come from the juncture's evaluation, so they are
// already health-aware.

import { useState } from "react";
import { CheckIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import Button, { ButtonSpinner } from "components/Button";

import type { QuotePaymentOption, QuotePlan } from "./api";
import ScreenChrome from "components/ScreenChrome";
import { PLAN_COPY, PLAN_ORDER } from "./PricingCards";
import { dollars, optionLabel, payTileDetail, termLabel } from "./payment-copy";

import {
  LINK_BUTTON_CLASS,
  OUTLINE_BUTTON_CLASS,
} from "components/FlowButtons";

/** The card-size transition's shared curve — the mockup's ease. */
const RESIZE_EASE = "cubic-bezier(.2,.8,.25,1)";

/** Display copy the pricing engine doesn't carry: what switching up from
 * Base actually buys. Authored sentences, not feature-list math — the
 * grid's feature bullets already do the math elsewhere. */
const UPSELL_COPY: Record<
  string,
  { pill: string; addsLabel: string; adds: string[] }
> = {
  value: {
    pill: "Your price never changes",
    addsLabel: "What Base doesn’t include",
    adds: [
      "Today’s price, locked in for life — cremation costs keep rising, yours wouldn’t",
      "Travel protection anywhere in the U.S., not just your state",
      "One certified death certificate, and we order more for your family for three months",
      "A handcrafted wooden urn instead of the simple scattering urn",
      "Certified delivery of remains anywhere in your state",
    ],
  },
  choice: {
    pill: "Nothing left to arrange",
    addsLabel: "Everything in Crest, plus",
    adds: [
      "Membership covers you anywhere in the world",
      "Four certificates, a full year of ordering, and we fix any errors",
      "A handcrafted metal urn, hand-delivered within 60 miles",
      "Account and asset discovery, plus a fingerprint keepsake",
    ],
  },
};

/** The card's headline option: the cheapest monthly way in. */
function startingOption(plan: QuotePlan): QuotePaymentOption | null {
  const monthly = plan.payment_options.filter(
    (option) => option.recurring_cents !== null,
  );
  if (monthly.length === 0) return null;
  return monthly.reduce((cheapest, option) =>
    (option.recurring_cents ?? 0) < (cheapest.recurring_cents ?? 0)
      ? option
      : cheapest,
  );
}

function singlePayOption(plan: QuotePlan): QuotePaymentOption | null {
  return (
    plan.payment_options.find(
      (option) => option.payment_period === "single_pay",
    ) ?? null
  );
}

/** How the keep band speaks the family's standing option. */
function keptOptionPhrase(option: QuotePaymentOption | null): string {
  if (!option) return "";
  if (option.recurring_cents === null) {
    return `${dollars(option.due_today_cents)} paid in full`;
  }
  const term = termLabel(option.term_months);
  return `${dollars(option.recurring_cents)}/mo${term ? ` for ${term}` : ""}`;
}

/** The price tile inside a card: starting monthly with its today amount
 * and the paid-in-full line — or the one payment, when the evaluation
 * left the plan pay-in-full only. */
function CardPriceTile({ plan, big }: { plan: QuotePlan; big: boolean }) {
  const monthly = startingOption(plan);
  const single = singlePayOption(plan);
  const priceSize = big
    ? "text-[31px] lg:text-[39px]"
    : "text-[25px] lg:text-[31px]";
  if (!monthly) {
    return (
      <div className="rounded-xl bg-oat px-[18px] py-4">
        <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-oat-800">
          {single ? optionLabel(single) : "Pricing"}
        </span>
        <div className="mt-1.5 flex flex-wrap items-baseline gap-1.5">
          <span
            className={`font-bold leading-none tracking-tight text-soil ${priceSize}`}
          >
            {single ? dollars(single.due_today_cents) : "—"}
          </span>
          {single ? (
            <span className="whitespace-nowrap text-sm text-oat-800">
              one payment
            </span>
          ) : null}
        </div>
      </div>
    );
  }
  const term = termLabel(monthly.term_months);
  return (
    <div className="rounded-xl bg-oat px-[18px] py-4">
      <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-oat-800">
        Starting at
      </span>
      <div className="mt-1.5 flex flex-wrap items-baseline gap-1.5">
        <span
          className={`font-bold leading-none tracking-tight text-soil ${priceSize}`}
        >
          {dollars(monthly.recurring_cents ?? 0)}
        </span>
        <span className="whitespace-nowrap text-sm text-oat-800">
          /mo{term ? ` for ${term}` : ""}
        </span>
      </div>
      {monthly.due_today_cents > 0 ? (
        <div className="mt-1.5 text-base text-oat-800">
          with {dollars(monthly.due_today_cents)} due today
        </div>
      ) : (
        <div className="mt-1.5 text-base text-oat-800">Nothing due today</div>
      )}
      {single ? (
        <div className="mt-2.5 border-t border-oat-200 pt-2.5 text-base leading-snug text-oat-800">
          or{" "}
          <span className="font-bold text-soil">
            {dollars(single.due_today_cents)}
          </span>{" "}
          paid in full today
        </div>
      ) : null}
    </div>
  );
}

function cardShadowClass(big: boolean, picked: boolean): string {
  if (picked) {
    return "shadow-[0_0_0_4px_rgba(255,184,133,0.4),0_18px_34px_-20px_rgba(80,58,58,0.45)]";
  }
  if (big) return "shadow-[0_18px_34px_-20px_rgba(80,58,58,0.45)]";
  return "shadow-none";
}

function switchButtonClass(big: boolean, picked: boolean): string {
  if (!big) {
    return "border-soil bg-transparent text-soil hover:border-cantelope hover:bg-cantelope";
  }
  if (picked) return "border-soil bg-soil text-ivory";
  return "border-cantelope bg-cantelope text-soil hover:border-soil hover:bg-soil hover:text-cantelope";
}

function UpsellCard({
  plan,
  big,
  picked,
  onPick,
  busy,
}: {
  plan: QuotePlan;
  big: boolean;
  picked: boolean;
  onPick: () => void;
  busy: boolean;
}) {
  const planCopy = PLAN_COPY[plan.slug];
  const copy = UPSELL_COPY[plan.slug];
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onPick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onPick();
        }
      }}
      className={`flex cursor-pointer flex-col rounded-2xl border-2 text-left transition-all duration-[450ms] ${cardShadowClass(
        big,
        picked,
      )} ${
        big
          ? "gap-[18px] border-cantelope bg-white p-6 sm:p-7"
          : "gap-3.5 border-oat-200 bg-oat-50 p-6"
      }`}
      style={{ transitionTimingFunction: RESIZE_EASE }}
    >
      <div className="flex min-h-6 items-center justify-between gap-3">
        <span
          className={`text-[13px] font-bold uppercase tracking-[0.14em] ${
            planCopy?.kickerClass ?? "text-evergreen"
          }`}
        >
          {planCopy?.kicker ?? plan.name}
        </span>
        {big && copy ? (
          <span className="flex-none whitespace-nowrap rounded-full bg-cantelope px-3 py-1 text-[13px] font-bold text-soil">
            {copy.pill}
          </span>
        ) : null}
      </div>

      <h2
        className={`font-serif leading-[1.1] text-soil ${
          big ? "text-[31px] lg:text-[39px]" : "text-[25px] lg:text-[31px]"
        }`}
      >
        {plan.name} <span className="text-oat-800">package</span>
      </h2>

      <CardPriceTile plan={plan} big={big} />

      {copy ? (
        <div>
          <div className="text-[13px] font-bold uppercase tracking-[0.12em] text-oat-800">
            {copy.addsLabel}
          </div>
          <div
            className={`mt-3 flex flex-col gap-[11px] text-base ${
              big ? "text-soil" : "text-oat-800"
            }`}
          >
            {copy.adds.map((line) => (
              <div key={line} className="flex items-start gap-2.5">
                <CheckIcon
                  className="mt-1 h-3.5 w-3.5 shrink-0 text-evergreen"
                  strokeWidth={3}
                  aria-hidden="true"
                />
                <span className="leading-snug">{line}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <button
        type="button"
        disabled={busy}
        onClick={(event) => {
          event.stopPropagation();
          onPick();
        }}
        className={`mt-auto inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 px-5 py-3.5 text-base font-bold transition-all ${switchButtonClass(
          big,
          picked,
        )}`}
      >
        {picked ? (
          <>
            <CheckIcon className="h-4 w-4" strokeWidth={3} aria-hidden="true" />
            Selected
          </>
        ) : (
          <>
            Switch to {plan.name}
            <ChevronRightIcon
              className="h-4 w-4"
              strokeWidth={2.5}
              aria-hidden="true"
            />
          </>
        )}
      </button>
    </div>
  );
}

/** The membership chip beside the payment heading — priced from the
 * plan's monthly way in, silent when nothing is due today. */
function MembershipNote({ plan }: { plan: QuotePlan }) {
  const membershipCents = startingOption(plan)?.due_today_cents ?? 0;
  if (membershipCents <= 0) return null;
  return (
    <div className="rounded-lg bg-oat-50 px-3.5 py-2.5 text-[13px] leading-[1.45] text-oat-700">
      Every option includes the {plan.name} Membership &mdash;{" "}
      {dollars(membershipCents)}
    </div>
  );
}

function PayOptionTile({
  option,
  selected,
  onSelect,
}: {
  option: QuotePaymentOption;
  selected: boolean;
  onSelect: () => void;
}) {
  const monthly = option.recurring_cents !== null;
  const term = termLabel(option.term_months);
  const detail = payTileDetail(option);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex h-full flex-col items-start gap-2.5 rounded-xl border-2 px-[18px] py-4 text-left transition-all ${
        selected
          ? "border-soil bg-oat"
          : "border-oat-200 bg-white hover:border-evergreen/50"
      }`}
    >
      <span className="flex w-full items-start gap-3">
        <span
          className={`mt-0.5 flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border-2 ${
            selected ? "border-soil" : "border-oat-300"
          }`}
          aria-hidden="true"
        >
          {selected ? (
            <span className="block h-2 w-2 rounded-full bg-soil" />
          ) : null}
        </span>
        <span className="min-w-0 flex-1 text-base font-bold leading-[1.35] text-soil">
          {optionLabel(option)}
        </span>
      </span>
      <span className="flex items-baseline gap-1.5">
        <span className="text-[31px] font-bold leading-none tracking-tight text-soil sm:text-[39px]">
          {dollars(
            monthly && option.recurring_cents !== null
              ? option.recurring_cents
              : option.due_today_cents,
          )}
        </span>
        <span className="text-base text-oat-700">
          {monthly ? `/mo${term ? ` for ${term}` : ""}` : "one payment"}
        </span>
      </span>
      <span className="text-[13px] leading-normal text-oat-700">{detail}</span>
      <span className="mt-auto w-full border-t border-oat-200 pt-2.5 text-[13px] font-bold text-soil">
        {option.due_today_cents > 0
          ? `Due today: ${dollars(option.due_today_cents)}`
          : "Nothing due today"}
      </span>
    </button>
  );
}

export interface UpsellScreenProps {
  /** The evaluation's plans, flag-filtered — Base included, so the quiz
   * can weigh it and the keep band can price it. */
  plans: QuotePlan[];
  /** The family's standing option as the evaluation offers it — what the
   * keep band prices. Null leaves the band priceless, never wrong. */
  currentOption: QuotePaymentOption | null;
  busy: boolean;
  problem: string | null;
  onKeep: () => void;
  onUpgrade: (choice: { plan_slug: string; option_slug: string }) => void;
  onBack: () => void;
}

/** The mockup's compare grid: the big card takes 1.45 shares, and the
 * shares trade sides (animated) as the big card moves. */
function upgradeGridColumns(
  upgrades: QuotePlan[],
  bigSlug: string | null,
): string {
  if (upgrades.length < 2) return "1fr";
  if (bigSlug === upgrades[0].slug) return "1.45fr 1fr";
  return "1fr 1.45fr";
}

export default function UpsellScreen({
  plans,
  currentOption,
  busy,
  problem,
  onKeep,
  onUpgrade,
  onBack,
}: UpsellScreenProps) {
  const [pickedSlug, setPickedSlug] = useState<string | null>(null);
  const [paySlug, setPaySlug] = useState<string | null>(null);

  const upgrades = plans
    .filter((plan) => plan.slug !== "simple")
    .sort((a, b) => PLAN_ORDER.indexOf(a.slug) - PLAN_ORDER.indexOf(b.slug));
  const picked = upgrades.find((plan) => plan.slug === pickedSlug) ?? null;
  // One card stands big at a time — the picked one, else the first
  // upgrade (Crest, when it's offered).
  const bigSlug = picked?.slug ?? upgrades[0]?.slug ?? null;
  const keptPhrase = keptOptionPhrase(currentOption);

  const pick = (plan: QuotePlan) => {
    setPickedSlug(plan.slug);
    // One way to pay picks itself — same rule as the pricing grid.
    setPaySlug(
      plan.payment_options.length === 1 ? plan.payment_options[0].slug : null,
    );
  };

  const gridColumns = upgradeGridColumns(upgrades, bigSlug);

  return (
    <ScreenChrome
      kicker="Before you pay"
      titleA="Base does the cremation."
      titleB={`${upgrades[0]?.name ?? "Crest"} does the rest.`}
      blurb="You picked Base, and it's a real plan — the cremation itself is fully covered. What it doesn't do is lock your price or travel with you. Switching now changes nothing you've already filled in."
      wide
    >
      <div
        className="mt-7 flex flex-col gap-4 md:grid md:items-start md:gap-[22px] md:transition-[grid-template-columns] md:duration-[450ms]"
        style={{
          gridTemplateColumns: gridColumns,
          transitionTimingFunction: RESIZE_EASE,
        }}
      >
        {upgrades.map((plan) => (
          <UpsellCard
            key={plan.slug}
            plan={plan}
            big={plan.slug === bigSlug}
            picked={plan.slug === pickedSlug}
            onPick={() => pick(plan)}
            busy={busy}
          />
        ))}
      </div>

      {problem && (
        <p className="mt-5 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base font-medium text-red-800">
          {problem}
        </p>
      )}

      {picked ? (
        <div className="mt-6 flex flex-col gap-5 rounded-2xl border-2 border-soil bg-white p-6 shadow-[0_18px_34px_-22px_rgba(80,58,58,0.4)] sm:p-7">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="text-[13px] font-bold uppercase tracking-[0.25em] text-evergreen">
                Now the payment
              </span>
              <div className="mt-2 font-serif text-[25px] leading-[1.2] text-soil sm:text-[31px]">
                How you&rsquo;d like to pay for{" "}
                <span className="font-sans font-bold text-evergreen">
                  {picked.name}
                </span>
              </div>
            </div>
            <MembershipNote plan={picked} />
          </div>
          <div className="flex flex-col gap-3 sm:grid sm:grid-cols-3 sm:items-stretch sm:gap-3.5">
            {picked.payment_options.map((option) => (
              <PayOptionTile
                key={option.slug}
                option={option}
                selected={paySlug === option.slug}
                onSelect={() => setPaySlug(option.slug)}
              />
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <Button
              variant="continue"
              disabled={paySlug === null}
              isLoading={busy}
              icon={
                <ChevronRightIcon
                  className="h-4 w-4"
                  strokeWidth={2.5}
                  aria-hidden="true"
                />
              }
              iconPosition="right"
              onClick={() => {
                if (paySlug !== null) {
                  onUpgrade({ plan_slug: picked.slug, option_slug: paySlug });
                }
              }}
            >
              Continue
            </Button>
            <button
              type="button"
              className={LINK_BUTTON_CLASS}
              disabled={busy}
              onClick={() => {
                setPickedSlug(null);
                setPaySlug(null);
              }}
            >
              Nevermind
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-6 flex flex-wrap items-center gap-5 rounded-xl bg-oat px-5 py-[18px] sm:px-[22px]">
          <p className="min-w-[220px] flex-1 text-base leading-normal text-oat-800">
            Base is still a complete cremation
            {keptPhrase ? (
              <>
                {" "}
                &mdash;{" "}
                <span className="font-bold text-soil">{keptPhrase}</span>
              </>
            ) : null}
            , no price lock, no travel coverage.
          </p>
          <div className="flex flex-none items-center gap-[18px]">
            <button
              type="button"
              className={LINK_BUTTON_CLASS}
              disabled={busy}
              onClick={onBack}
            >
              Back
            </button>
            <button
              type="button"
              className={OUTLINE_BUTTON_CLASS}
              disabled={busy}
              aria-busy={busy || undefined}
              onClick={onKeep}
            >
              {busy && <ButtonSpinner />}
              Continue with Base
              <ChevronRightIcon
                className="h-4 w-4"
                strokeWidth={2.5}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      )}
    </ScreenChrome>
  );
}
